import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Analytics } from "@vercel/analytics/react"
import { type ComponentType, type FC, useState } from "react"
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router"
import { Tooltip } from "react-tooltip"

import "@forento/ui/assets/lexical.css"
import { type FCC } from "@forento/ui/types/react"

import Loader from "~/components/loader"
import { useUserContext } from "~/context/user"
import UserProvider from "~/context/user/Provider"
import BuildYourOwnPage from "~/pages/build-your-own"
import BuildYourOwnSuccessPage from "~/pages/build-your-own-success"
import CoursePage from "~/pages/course/course"
import CoursesPage from "~/pages/course/courses"
import CreateCoursePage from "~/pages/course/create"
import SalesPage from "~/pages/sales"
import SettingsPage from "~/pages/user/settings"
import SignInPage from "~/pages/user/sign-in"
import SignUpPage from "~/pages/user/sign-up"
import VideoPage from "~/pages/video/video"
import VideosPage from "~/pages/video/videos"
import trpc, { query } from "~/utilities/trpc"

type AppRoute = { path: string; element: ComponentType; requireAuth?: boolean }
const routes: AppRoute[] = [
	{ path: "/courses", element: CoursesPage, requireAuth: true },
	{ path: "/courses/create", element: CreateCoursePage, requireAuth: true },
	{ path: "/courses/:courseId", element: CoursePage, requireAuth: true },
	{ path: "/videos", element: VideosPage, requireAuth: true },
	{ path: "/videos/:videoId", element: VideoPage, requireAuth: true },
	{ path: "/sales", element: SalesPage, requireAuth: true },
	{ path: "/build-your-own", element: BuildYourOwnPage, requireAuth: true },
	{ path: "/build-your-own/success", element: BuildYourOwnSuccessPage, requireAuth: true },
	{ path: "/user/settings", element: SettingsPage, requireAuth: true },
	{ path: "/user/sign-in", element: SignInPage },
	{ path: "/user/sign-up", element: SignUpPage },
]

const App: FC = () => {
	const [queryClient] = useState(() => new QueryClient())

	return (
		<query.Provider client={trpc} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<UserProvider>
					<Analytics />
					<BrowserRouter>
						<Routes>
							<Route path="/videor" element={<Navigate to="/videos" replace />} />
							<Route path="/videor/:videoId" element={<LegacyVideoRouteRedirect />} />
							{routes.map(({ path, element: Element, requireAuth }) => (
								<Route
									key={path}
									path={path}
									element={
										requireAuth ? (
											<AuthWrapper>
												<Element />
											</AuthWrapper>
										) : (
											<Element />
										)
									}
								/>
							))}
							<Route path="*" element={<Navigate to="/courses" replace />} />
						</Routes>
					</BrowserRouter>
					<Tooltip id="tooltip" />
				</UserProvider>
			</QueryClientProvider>
		</query.Provider>
	)
}

const AuthWrapper: FCC = ({ children }) => {
	const location = useLocation()
	const user = useUserContext()

	if (user.isLoading)
		return (
			<div className="flex h-screen w-screen items-center justify-center">
				<Loader label="Laddar..." />
			</div>
		)

	if (user.user === null)
		return <Navigate to={`/user/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} />

	return <>{children}</>
}

const LegacyVideoRouteRedirect: FC = () => {
	const { videoId } = useParams<{ videoId: string }>()
	const location = useLocation()

	return <Navigate to={`/videos/${videoId}${location.search}`} replace />
}

export default App
